import React from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
function useWvcLanguage(): "en" | "zh" | "fr" | "es" | "de" {
  const [language, setLanguage] = React.useState<"en" | "zh" | "fr" | "es" | "de">("en");
  React.useEffect(() => {
    const updateLang = () => {
      const docLang = document.documentElement.lang || "en";
      const shortLang = docLang.split("-")[0].toLowerCase();
      if (["en", "zh", "fr", "es", "de"].includes(shortLang)) {
        setLanguage(shortLang as "en" | "zh" | "fr" | "es" | "de");
      } else {
        setLanguage("en");
      }
    };
    updateLang();
    const observer = new MutationObserver(updateLang);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["lang"]
    });
    return () => observer.disconnect();
  }, []);
  return language;
}
const translations = {
  en: {
    lookbooks: "Seasonal Lookbooks",
    curated: "Curated seasonal",
    capsules: "capsules",
    description: "Distinct edits capturing modern silhouettes, intentional cuts, and tactile European fabrics.",
    occasion: "Occasion Edit",
    occasionDesc: "Fluid evening silhouettes and refined silk separates tailored for memorable gatherings.",
    occasionCta: "Explore Occasion",
    holiday: "Holiday Collection",
    holidayDesc: "Plush cashmere knitwear and rich textures styled for effortless cold-weather warmth.",
    holidayCta: "Discover Holiday",
    tailoring: "Soft Tailoring",
    tailoringDesc: "Relaxed double-breasted blazers and structured wide-leg trousers crafted for daily ease.",
    tailoringCta: "Shop Tailoring"
  },
  zh: {
    lookbooks: "季节性查看手册",
    curated: "精心策划的季节",
    capsules: "胶囊",
    description: "独特的编辑，捕捉现代剪裁、有意的剪裁和触觉欧洲织物。",
    occasion: "场合编辑",
    occasionDesc: "流体晚礼服剪裁和精致的丝绸单品，为难忘的聚会而设计。",
    occasionCta: "探索场合",
    holiday: "假日系列",
    holidayDesc: "毛茸茸的羊绒针织品和丰富的纹理，为轻松的冷天温暖而设计。",
    holidayCta: "发现假日",
    tailoring: "柔软裁剪",
    tailoringDesc: "轻松双排扣夹克和结构化直筒裤，为日常便利而设计。",
    tailoringCta: "购物裁剪"
  },
  fr: {
    lookbooks: "Lookbooks Saisonniers",
    curated: "Saisonnier Curé",
    capsules: "capsules",
    description: "Des édits distincts capturant les silhouettes modernes, les coupes intentionnelles et les tissus européens tactiles.",
    occasion: "Édition Occasion",
    occasionDesc: "Silhouettes de soirée fluides et séparés de soie raffinés taillés pour des rassemblements mémorables.",
    occasionCta: "Explorer l'Occasion",
    holiday: "Collection Vacances",
    holidayDesc: "Tricots en cachemire doux et textures riches stylisés pour la chaleur sans effort par temps froid.",
    holidayCta: "Découvrir les Vacances",
    tailoring: "Tailleur Doux",
    tailoringDesc: "Blazers détendus à double boutonnage et pantalons larges structurés conçus pour la facilité quotidienne.",
    tailoringCta: "Acheter la Tailleur"
  },
  es: {
    lookbooks: "Lookbooks Estacionales",
    curated: "Estacional Curado",
    capsules: "cápsulas",
    description: "Ediciones distintas que capturan siluetas modernas, cortes intencionales y telas europeas táctiles.",
    occasion: "Edición de Ocasión",
    occasionDesc: "Siluetas de noche fluidas y separados de seda refinados diseñados para reuniones memorables.",
    occasionCta: "Explorar Ocasión",
    holiday: "Colección Vacaciones",
    holidayDesc: "Prendas de cashmere suave y texturas ricas diseñadas para calidez sin esfuerzo en clima frío.",
    holidayCta: "Descubrir Vacaciones",
    tailoring: "Sastrería Suave",
    tailoringDesc: "Blazers relajados de doble botonadura y pantalones de pierna ancha estructurados diseñados para facilidad diaria.",
    tailoringCta: "Comprar Sastrería"
  },
  de: {
    lookbooks: "Saisonale Lookbooks",
    curated: "Kuratierter Saisonale",
    capsules: "Kapseln",
    description: "Unterschiedliche Bearbeitungen, die moderne Silhouetten, absichtliche Schnitte und tastbare europäische Stoffe erfassen.",
    occasion: "Gelegenheitsausgabe",
    occasionDesc: "Flüssige Abendsil houetten und verfeinerte Seidenseparaten für unvergessliche Versammlungen.",
    occasionCta: "Gelegenheit Erkunden",
    holiday: "Weihnachtskollektion",
    holidayDesc: "Plüsch-Kaschmir-Strickwaren und reichhaltige Texturen für mühelosen Wärmeschutz bei kaltem Wetter.",
    holidayCta: "Weihnachten Entdecken",
    tailoring: "Weiches Schneidern",
    tailoringDesc: "Entspannte Doppelreihige Blazer und strukturierte Hosen mit weitem Bein für tägliche Leichtigkeit.",
    tailoringCta: "Schneidern Kaufen"
  }
};
const capsuleConfigs = [{
  titleKey: "occasion" as const,
  summaryKey: "occasionDesc" as const,
  ctaKey: "occasionCta" as const,
  to: "/ShopPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_occasion_edit.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_occasion_edit.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_occasion_edit.webp-wvc-sizes",
    alt: "Model wearing dark evening silk gown from the Occasion Edit"
  }
}, {
  titleKey: "holiday" as const,
  summaryKey: "holidayDesc" as const,
  ctaKey: "holidayCta" as const,
  to: "/ShopPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_holiday_series.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_holiday_series.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_holiday_series.webp-wvc-sizes",
    alt: "Model in warm tailored knitwear from the Holiday Collection"
  }
}, {
  titleKey: "tailoring" as const,
  summaryKey: "tailoringDesc" as const,
  ctaKey: "tailoringCta" as const,
  to: "/ShopPage",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_soft_tailoring.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_soft_tailoring.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1794104/img/collection_soft_tailoring.png-wvc-sizes",
    alt: "Model styled in relaxed minimal tailoring suit"
  }
}];
export default function Collections() {
  const language = useWvcLanguage();
  const t = translations[language] || translations.en;
  return <section id="系列展示" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="max-w-[1360px] mx-auto px-6 sm:px-10 lg:px-16">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 md:mb-16 pb-6 border-b border-border">
          <div className="max-w-xl">
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold block mb-3">
              {t.lookbooks}
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground">
              {t.curated} <span className="italic font-serif">{t.capsules}</span>
            </h2>
          </div>
          <p className="mt-4 md:mt-0 text-sm md:text-base text-muted-foreground max-w-sm">
            {t.description}
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {capsuleConfigs.map((capsule, i) => <div key={capsule.titleKey} data-index={i} className="group flex flex-col">
              <Card className="flex-1 bg-card text-card-foreground border border-border rounded-none shadow-xs overflow-hidden flex flex-col p-0 transition-colors duration-200">
                {/* 3:4 Aspect Ratio Portrait Image */}
                <div className="relative overflow-hidden bg-muted">
                  <AspectRatio ratio={3 / 4}>
                    <img src={capsule.image.src} data-wvc-srcset={capsule.image.srcset} data-wvc-sizes={capsule.image.sizes} alt={capsule.image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-103" />
                  </AspectRatio>
                </div>

                {/* Card Content */}
                <CardContent className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="font-serif text-2xl font-normal tracking-tight text-card-foreground mb-3">
                      {t[capsule.titleKey]}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t[capsule.summaryKey]}
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-border">
                    <Button variant="secondary" size="default" asChild className="w-full rounded-none justify-between text-xs tracking-wider uppercase font-semibold h-11 px-5">
                      <Link to={capsule.to}>
                        <span>{t[capsule.ctaKey]}</span>
                        <ArrowUpRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}